/**
 * --------------------------------------------------------------------------
 * Liander Bootstrap flex-carousel.js
 * --------------------------------------------------------------------------
 */

import { defineJQueryPlugin } from '../../node_modules/bootstrap/js/src/util/index.js'
import EventHandler from '../../node_modules/bootstrap/js/src/dom/event-handler.js'
import Manipulator from '../../node_modules/bootstrap/js/src/dom/manipulator.js'
import SelectorEngine from '../../node_modules/bootstrap/js/src/dom/selector-engine.js'
import BaseComponent from '../../node_modules/bootstrap/js/src/base-component.js'

/**
 * Constants
 */

const NAME = 'flexcarousel'
const DATA_KEY = 'lbs.flex-carousel'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'

const ARROW_LEFT_KEY = 'ArrowLeft'
const ARROW_RIGHT_KEY = 'ArrowRight'
const TRANSITION_DURATION = 50

const DIRECTION_LEFT = 'left'
const DIRECTION_RIGHT = 'right'

const EVENT_SLIDE = `slide${EVENT_KEY}`
const EVENT_KEYDOWN = `keydown${EVENT_KEY}`
const EVENT_LOAD_DATA_API = `load${EVENT_KEY}${DATA_API_KEY}`
const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`

const CLASS_NAME_AT_BEGIN = 'at-begin'
const CLASS_NAME_AT_END = 'at-end'
const CLASS_NAME_CAROUSEL = 'flex-carousel'
const CLASS_NAME_NOT_SLIDING = 'is-set'
const CLASS_NAME_REVERSE_SLIDING = 'is-reversing'
const CLASS_NAME_REFERENCE_ITEM = 'is-ref'

const SELECTOR_CAROUSEL = '.flex-carousel'
const SELECTOR_CAROUSEL_INNER = '.flex-carousel-inner'
const SELECTOR_DATA_SLIDE = '[data-bs-slide]'
const SELECTOR_ITEM = '.flex-carousel-item'
const SELECTOR_REFERENCE_ITEM = '.is-ref'

const KEY_TO_DIRECTION = {
  [ARROW_LEFT_KEY]: DIRECTION_RIGHT,
  [ARROW_RIGHT_KEY]: DIRECTION_LEFT
}

const Default = {
  goRound: false,
  keyboard: true
}

const DefaultType = {
  goRound: 'boolean',
  keyboard: 'boolean'
}

/**
 * Class definition
 */

class FlexCarousel extends BaseComponent {
  constructor(element, config) {
    super(element, config)

    this._xDown = null
    this._yDown = null

    this.initFlexCarousel()

    this._addEventListeners()
    this._setBeginEndClass()
  }

  // Getters
  static get Default() {
    return Default
  }

  static get DefaultType() {
    return DefaultType
  }

  static get NAME() {
    return NAME
  }

  // Public
  initFlexCarousel() {
    this._initializeReferenceItem()
    this._handleSlideTo()
  }

  slidePrevious(trigger) {
    const refElement = this._getReferenceItem()
    if (this._config.goRound || !this._firstItemVisible(refElement)) {
      refElement.classList.remove(CLASS_NAME_REFERENCE_ITEM)
      this._getCarouselInner().classList.add(CLASS_NAME_REVERSE_SLIDING)
      this._slide(this._getPreviousCarouselItem(refElement))
      EventHandler.trigger(this._element, EVENT_SLIDE, {
        direction: DIRECTION_LEFT,
        trigger
      })
    }
  }

  slideNext(trigger) {
    const refElement = this._getReferenceItem()
    if (this._config.goRound || !this._lastItemVisible(refElement)) {
      refElement.classList.remove(CLASS_NAME_REFERENCE_ITEM)
      this._getCarouselInner().classList.remove(CLASS_NAME_REVERSE_SLIDING)
      this._slide(this._getNextCarouselItem(refElement))
      EventHandler.trigger(this._element, EVENT_SLIDE, {
        direction: DIRECTION_RIGHT,
        trigger
      })
    }
  }

  // Private
  _addEventListeners() {
    if (this._config.keyboard) {
      EventHandler.on(this._element, EVENT_KEYDOWN, event => this._keydown(event))
    }

    EventHandler.on(this._element, 'touchstart', event => this._handleTouchStart(event), {
      passive: true,
      capture: false
    })
    EventHandler.on(this._element, 'touchmove', event => this._handleTouchMove(event), {
      passive: true,
      capture: false
    })
  }

  _keydown(event) {
    if (/input|textarea/i.test(event.target.tagName)) {
      return
    }

    const direction = KEY_TO_DIRECTION[event.key]
    if (direction) {
      event.preventDefault()
      if (direction === DIRECTION_LEFT) {
        this.slideNext('keydown')
      }

      if (direction === DIRECTION_RIGHT) {
        this.slidePrevious('keydown')
      }
    }
  }

  _getCarouselInner() {
    return SelectorEngine.findOne(SELECTOR_CAROUSEL_INNER, this._element)
  }

  _getReferenceItem() {
    return SelectorEngine.findOne(SELECTOR_REFERENCE_ITEM, this._element)
  }

  _getItems() {
    return SelectorEngine.find(SELECTOR_ITEM, this._element)
  }

  _getNextCarouselItem(element) {
    const items = this._getItems()
    const elementIndex = items.indexOf(element)
    return elementIndex === items.length - 1 ? items[0] : items[elementIndex + 1]
  }

  _getPreviousCarouselItem(element) {
    const items = this._getItems()
    const elementIndex = items.indexOf(element)
    return elementIndex === 0 ? items[items.length - 1] : items[elementIndex - 1]
  }

  _handleSlideTo() {
    const slideIndex = this._element.getAttribute('data-bs-slide-to')
    if (slideIndex && slideIndex < this._getItems().length && slideIndex > 0) {
      for (let index = 0; index < slideIndex; index++) {
        this.slideNext('auto')
      }
    }
  }

  _initializeReferenceItem() {
    const items = this._getItems()
    for (const refElement of items) {
      refElement.classList.remove(CLASS_NAME_REFERENCE_ITEM)
    }

    items[items.length - 1].classList.add(CLASS_NAME_REFERENCE_ITEM)
  }

  _lastItemVisible(element) {
    const hundred = 100
    const visibleItems = Math.round(hundred / getComputedStyle(element).flexBasis.slice(0, -1))
    const nextElement = this._getNextCarouselItem(element)
    const items = this._getItems()
    const nextElIndex = items.indexOf(nextElement)

    return items.length - nextElIndex <= visibleItems
  }

  _firstItemVisible(element) {
    const nextElementIndex = this._getItems().indexOf(this._getNextCarouselItem(element))
    return nextElementIndex === 0
  }

  _slide(firstItem) {
    const two = 2
    let i = two
    let j = two
    firstItem.classList.add(CLASS_NAME_REFERENCE_ITEM)
    firstItem.style.order = 1

    for (let ref = this._getItems().length; two <= ref ? j <= ref : j >= ref; i = two <= ref ? ++j : --j) {
      firstItem = this._getNextCarouselItem(firstItem)
      firstItem.style.order = i
    }

    this._getCarouselInner().classList.remove(CLASS_NAME_NOT_SLIDING)
    setTimeout(() => this._postSlideActions(), TRANSITION_DURATION)
  }

  _postSlideActions() {
    this._getCarouselInner().classList.add(CLASS_NAME_NOT_SLIDING)
    this._setBeginEndClass()
  }

  _setBeginEndClass() {
    const refElement = this._getReferenceItem()
    if (this._firstItemVisible(refElement)) {
      this._element.classList.add(CLASS_NAME_AT_BEGIN)
      this._element.classList.remove(CLASS_NAME_AT_END)
    } else if (this._lastItemVisible(refElement)) {
      this._element.classList.add(CLASS_NAME_AT_END)
      this._element.classList.remove(CLASS_NAME_AT_BEGIN)
    } else {
      this._element.classList.remove(CLASS_NAME_AT_BEGIN)
      this._element.classList.remove(CLASS_NAME_AT_END)
    }
  }

  _getTouches(event) {
    return event.touches || event.originalEvent.touches
  }

  _handleTouchStart(event) {
    const firstTouch = this._getTouches(event)[0]
    this._xDown = firstTouch.clientX
    this._yDown = firstTouch.clientY
  }

  _isHorizontalSwipe(xDiff, yDiff) {
    return Math.abs(xDiff) > Math.abs(yDiff)
  }

  _isLeftSwipe(xDiff) {
    return xDiff > 0
  }

  _handleTouchMove(event) {
    if (!this._xDown || !this._yDown) {
      return
    }

    const xUp = event.touches[0].clientX
    const yUp = event.touches[0].clientY
    const xDiff = this._xDown - xUp
    const yDiff = this._yDown - yUp

    if (this._isHorizontalSwipe(xDiff, yDiff)) {
      if (this._isLeftSwipe(xDiff)) {
        this.slideNext('swipe')
      } else {
        this.slidePrevious('swipe')
      }
    }

    this._xDown = null
    this._yDown = null
  }

  // Static

  static jQueryInterface(config) {
    return this.each(function () {
      const data = FlexCarousel.getOrCreateInstance(this, config)

      if (typeof config === 'number') {
        data.to(config)
        return
      }

      if (typeof config === 'string') {
        if (data[config] === undefined || config.startsWith('_') || config === 'constructor') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config]()
      }
    })
  }
}

/**
 * Data API implementation
 */

EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_SLIDE, function (event) {
  const target = SelectorEngine.getElementFromSelector(this)

  if (!target || !target.classList.contains(CLASS_NAME_CAROUSEL)) {
    return
  }

  event.preventDefault()

  const carousel = FlexCarousel.getOrCreateInstance(target)

  if (Manipulator.getDataAttribute(this, 'slide') === 'next') {
    carousel.slideNext('click')
  } else {
    carousel.slidePrevious('click')
  }
})

EventHandler.on(window, EVENT_LOAD_DATA_API, () => {
  const carousels = SelectorEngine.find(SELECTOR_CAROUSEL)

  for (const carousel of carousels) {
    FlexCarousel.getOrCreateInstance(carousel)
  }
})

/**
 * jQuery
 */

defineJQueryPlugin(FlexCarousel)

export default FlexCarousel
